import {
  Component,
  Event,
  EventEmitter,
  Host,
  Listen,
  State,
  h,
} from '@stencil/core';
import { Unsubscribe, store } from '@stencil/redux';
import {
  AccessCodeEncryptedInfo,
  TinWebChallenge,
  TinWebResponseInfo,
} from '../../class/class';
import { Language } from '../../enums/const-enums';
import { uiReady } from '../../redux/actions/config-actions';
import { RootState, initialStore } from '../../redux/store';
import { SignSubmit } from '../../service/sign-submit';
import { focusSignElement, handleErrorResponse } from '../../utils/utils';
import {
  RassTinwebTranslations,
  rassTinwebTranslations,
} from './i18n/rass-tinweb.i18n';

@Component({
  tag: 'rass-tinweb',
  styleUrl: 'rass-tinweb.scss',
  shadow: false,
})
export class RassTinweb {
  @State() assetsPath: string;
  @State() language: Language;
  @State() challengeData: TinWebChallenge;
  @State() summary: string;
  @Event({ eventName: 'tinCode:reset' }) tinCodeResetEvent: EventEmitter;
  uiReady!: typeof uiReady;
  unsubscribe!: Unsubscribe;
  maxTinWebCodeLength = 5;

  componentWillLoad(): void {
    store.setStore(initialStore);
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      const {
        config: { assetsPath, language, summary },
        challengeData,
      } = state;
      return { assetsPath, language, summary, challengeData };
    });
    store.mapDispatchToProps(this, {
      uiReady,
    });
  }

  componentDidLoad(): void {
    this.uiReady();
  }

  @Listen('tinCode:change')
  handleTinCodeChange(event: CustomEvent<AccessCodeEncryptedInfo>): void {
    const accessCodeEncryptedInfo: AccessCodeEncryptedInfo = event.detail;
    const responseInfo = new TinWebResponseInfo(
      accessCodeEncryptedInfo.ecdhe_client_pubkey,
      accessCodeEncryptedInfo.access_code_encrypted,
      this.challengeData.challenge_id,
    );
    new SignSubmit()
      .doSignSubmit(responseInfo)
      .catch((error: AppModel.HttpResponseErrorJson) => {
        handleErrorResponse(error);
        this.resetTinCodeEvent();
      });
  }

  @Listen('click')
  handleHostClick(): void {
    focusSignElement('rass-tin-code-input', { preventScroll: true });
  }

  get challengeInstructions(): string {
    return this.translations.instructions.sign;
  }
  get translations(): RassTinwebTranslations {
    return (
      rassTinwebTranslations[this.language] ||
      rassTinwebTranslations[Language.EN]
    );
  }
  private resetTinCodeEvent(): void {
    this.tinCodeResetEvent.emit();
  }

  render(): void {
    return (
      <Host class="section-tinweb">
        <style>@import url({`'${this.assetsPath}css/sfc-style.css'`});</style>
        <div class="sfc-container body-width" role="form">
          <rass-tin-input
            tinCodeMaxLength={this.maxTinWebCodeLength}
            ecdheServerPubKey={this.challengeData.ecdhe_server_pubkey}
            challengeInstructions={this.challengeInstructions}
            summary={this.summary}
          />
          <div class="challenge-section sfc-row sfc-justify-content-center sfc-padding-block-end-300 sfc-margin-block-end-300">
            <div class="sfc-col-12">
              <p
                id="challenge-instructions"
                class="sfc-paragraph sfc-text--size-md sfc-font-text-align-center sfc-margin-block-start-200"
                innerHTML={this.challengeInstructions}
              />
            </div>
          </div>
        </div>
      </Host>
    ) as void;
  }
}
